import React, { useContext, useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import axios from "axios";
import { useFormik } from "formik";
import { setAccessToken, setRefreshToken } from "../../src/storage/storage";
import { ButtonLoader4 } from "../../src/components/ButtonLoader";
import GlobalContext from "../../src/context/GlobalContext";
import { GoEye, GoEyeClosed } from "react-icons/go";
import Swal from "sweetalert2";

// Password Validation Function
const validatePassword = (password) => {
  return {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    // specialChar: /[@#%^&*]/.test(password),
    specialChar: /[!@#$%^&*()_+={}\[\]:;"'<>,.?/\\|`~]/.test(password),
    noSpaces: !/\s/.test(password),
  };
};

export const ContactFormSchema = Yup.object({
  email: Yup.string().email().required("Enter your email"),
  password: Yup.string().required("Enter your password"),
});

const initialValues = {
  email: "",
  password: "",
};

const LoginForm = () => {
  const { setUserData } = useContext(GlobalContext);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
    noSpaces: false,
  });
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues,
    validationSchema: ContactFormSchema,
    validate: (values) => {
      const validations = validatePassword(values.password);
      setPasswordValidations(validations);

      const allValid = Object.values(validations).every(Boolean);
      if (!allValid) {
        return { password: "Password does not meet all requirements" };
      }
    },
    onSubmit: async (values, actions) => {
      setLoading(true);
      try {
        const response = await axios.post(`${BASE_URL}/api/login/`, {
          ...values,
          platform: "web",
        });
        setUserData(response.data.user);
        setAccessToken(response.data.access);
        setRefreshToken(response.data.refresh);
        sessionStorage.setItem(
          "companyInfo",
          JSON.stringify(response.data.user.company_information_added)
        );
        sessionStorage.setItem(
          "PropertyData",
          JSON.stringify(response.data.user.import_lodgify)
        );
        sessionStorage.setItem(
          "CleanerData",
          JSON.stringify(response.data.user.cleaner_data)
        );
        // toast.success("Login successful");
        Swal.fire({
          icon: "success",
          text: response.data.message || "Login Successful",
          customClass: {
            confirmButton: "custom-green-button",
          },
        }).then(() => {
          // Always redirect to "/" after login - let the Routing component handle intended route logic
          navigate("/");
        });
        // navigate(response.data.user.company_information_added ? "/stays" : "/");
        actions.resetForm();
      } catch (error) {
        // toast.error(
        //   error.response?.data?.non_field_errors || "Something went wrong");
        Swal.fire({
          icon: "error",
          text:
            error?.response?.data.non_field_errors ||
            "Please give us a moment to fix it. Feel free to try again later",
          customClass: {
            confirmButton: "custom-red-button",
          },
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <section className="flex-1 lg:max-w-[450px]">
      <div className="bg-white rounded-2xl p-12 shadow-sm">
        <h1 className="text-3xl font-bold text-[#586989] mb-2">Welcome Back</h1>
        <p className="text-gray-500 mb-8 text-sm">
          Sign in to your HouseKeeper property owners dashboard
        </p>

        <form onSubmit={formik.handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block font-medium text-gray-700 mb-2 text-sm"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-[#586989] focus:ring-3 focus:ring-[#586989]/10"
              placeholder="your.email@example.com"
              required
            />
            {formik.errors.email && formik.touched.email && (
              <p className="text-red-500 text-xs h-3 pt-1 pb-2">
                {formik.errors.email}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block font-medium text-gray-700 mb-2 text-sm"
            >
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-[#586989] focus:ring-3 focus:ring-[#586989]/10 pr-12"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {showPassword ? <GoEye size={20} /> : <GoEyeClosed size={20} />}
              </button>
            </div>
            <div className="flex justify-end mt-2">
              <Link
                to="/forgot-password"
                className="text-[#586989] text-sm font-medium hover:text-[#106A3A]"
              >
                Forgot Password?
              </Link>
            </div>
            {formik.errors.password && formik.touched.password && (
              <p className="text-red-500 text-xs h-3 pt-1 pb-2">
                {formik.errors.password}
              </p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="text-sm text-black font-normal">
            <p className="font-medium mb-2">Password must include:</p>
            <ul className="space-y-1">
              {[
                { label: "At least 8 characters", key: "length" },
                {
                  label: "At least one uppercase letter (A-Z)",
                  key: "uppercase",
                },
                {
                  label: "At least one lowercase letter (a-z)",
                  key: "lowercase",
                },
                { label: "At least one number (0-9)", key: "number" },
                {
                  label: "At least one special character (!@#$%^&*)",
                  key: "specialChar",
                },
                { label: "No spaces", key: "noSpaces" },
              ].map((item) => (
                <li
                  key={item.key}
                  className={`flex items-center text-xs ${
                    passwordValidations[item.key]
                      ? "text-gray-700"
                      : "text-red-500"
                  }`}
                >
                  {passwordValidations[item.key] ? (
                    <FaCheck
                      className="text-green-500 mr-2 flex-shrink-0"
                      size={12}
                    />
                  ) : (
                    <FaTimes
                      className="text-red-500 mr-2 flex-shrink-0"
                      size={12}
                    />
                  )}
                  {item.label}
                </li>
              ))}
            </ul>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-[#586989] text-[#FFF5CE] border-none rounded-lg text-base font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <ButtonLoader4 /> : "Sign In"}
          </button>
        </form>

        <div className="flex items-center text-center my-6 text-gray-400 text-sm">
          <div className="flex-1 border-b border-gray-200 mr-4"></div>
          or
          <div className="flex-1 border-b border-gray-200 ml-4"></div>
        </div>

        <p className="text-center text-gray-500 text-sm">
          Don't have an account?{" "}
          <Link
            to="/signup"
            className="text-[#586989] font-semibold no-underline hover:text-[#106A3A]"
          >
            Create one now
          </Link>
        </p>

        <p className="text-center text-gray-500 text-sm mt-2">
          Download app from App store,{" "}
          <Link
            to="/app-link"
            className="text-[#586989] font-semibold no-underline hover:text-[#106A3A]"
          >
            click here
          </Link>
        </p>
      </div>
    </section>
  );
};

export default LoginForm;
